/**
 * QBase 호출 클라이언트 — LiveSQL 테넌트 편입 1단계
 *
 * 예전에는 Node 백엔드(coreServerClient.js)가 서비스계정(admin1)으로 QBase 에 로그인해
 * 모든 사용자의 요청을 대신 보냈다. 그래서 서버가 보는 호출자는 언제나 한 사람이었고,
 * 제출자·조회자는 요청 본문이 주장하는 값일 뿐이었다.
 * 이제 브라우저가 **자기 자신으로** QBase 세션을 갖는다. 그 세션이 통하는 유일한 문이 이 파일이다.
 *
 * 규칙
 *  · credentials:'include' — 인증은 JSESSIONID 쿠키(path=/)다. 토큰을 만들지 않는다.
 *  · 실패를 삼키지 않는다 — 호출자가 "빈 목록"과 "못 불러옴"을 구분할 수 있어야 한다.
 *  · 응답 표준은 { isSuccess:'Y'|'N', resultList:[...] } 이지만 경로마다 편차가 있어 여기서 흡수한다.
 */

import { QBASE_API, AUTH_API, DEVICE_ID, QBASE_SYS_ID } from '../config/qbase';
import i18n from '../i18n';
import { detectMaintenance, getMaintenance, headerPhaseOf, noteMaintenance } from './qbaseMaintenance';

export class QbaseError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'QbaseError';
    this.status = status;
  }
}

function url(path, params) {
  const u = `${QBASE_API}${path}`;
  if (!params) return u;
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    // 한글 파라미터명이라 키도 인코딩해야 한다
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `${u}?${qs}` : u;
}

/**
 * QBase 점검(쓰기 동결)으로 막힌 응답 — 실패 문구를 「점검 중이라 저장 못 함」 으로 바꿔 던진다.
 * errorType 을 붙여 formatApiError 가 같은 문구(common:errors.QBASE_MAINTENANCE)로 번역한다.
 * **다시 보내지 않는다** — 자동 재전송은 저장을 두 번 할 수 있다. 입력은 부른 화면이 그대로 둔다.
 */
function maintenanceError(res, body, m) {
  // 상태는 늘 503 — 재기동 사이의 502 를 그대로 두면 화면이 「모델 오류」 같은 다른 사유로 읽는다
  const err = new QbaseError(i18n.t('common:errors.QBASE_MAINTENANCE'), 503);
  err.httpStatus = res && res.status;
  err.code = 'MAINTENANCE';
  err.maintenance = m;
  err.body = { ...(body && typeof body === 'object' ? body : {}), errorType: 'QBASE_MAINTENANCE' };
  return err;
}

/** 응답 본문 해석 — 표준 실패 표기 3종(비2xx · isSuccess:'N' · success:false)을 한 곳에서 던진다 */
async function parse(res) {
  const phase = headerPhaseOf(res);
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    if (phase) detectMaintenance(res.status, null, phase);
    if (!res.ok) throw new QbaseError(i18n.t('common:qb.requestFailed', { status: res.status }), res.status);
    return null;
  }
  const m = detectMaintenance(res.status, body, phase);
  if (m) throw maintenanceError(res, body, m);
  if (!res.ok) {
    // resultMessage — 등록 저장 API 의 검증 실패(400) 본문(2026-10-06 챌린지 저장 API)
    const msg = body?.message || body?.error || body?.resultMsg || body?.resultMessage || i18n.t('common:qb.requestFailed', { status: res.status });
    const err = new QbaseError(msg, res.status);
    err.body = body;
    throw err;
  }
  if (body && (body.isSuccess === 'N' || body.success === false)) {
    const err = new QbaseError(body.message || body.error || body.resultMsg || body.resultMessage || i18n.t('common:qb.requestFailedGeneric'), res.status);
    // **응답 본문을 붙여 보낸다.** QBase 는 실패 응답에 처리 지시를 함께 싣는다 —
    // 탈회 계정 재가입이면 withdrawn:true 와 usrid 가 여기 들어 있고, 그것 없이는 화면이
    // "실패했다" 만 말하고 사용자를 막다른 길에 세운다.
    err.body = body;
    throw err;
  }
  return body;
}

/**
 * DB 전환 마지막의 QBase 재기동(접속 거부 · 프록시 502/504) — 마지막으로 본 단계가 SWITCHING 이면 장애가 아니라 점검이다.
 * 그 밖의 끊김은 원래대로 둔다.
 */
function switchingGap(status) {
  const cur = getMaintenance();
  if (!cur.active || cur.phase !== 'SWITCHING') return null;
  noteMaintenance({ ...cur, restarting: true }, 'SWITCHING');
  return { status, m: getMaintenance() };
}

async function send(target, init) {
  let res;
  try {
    res = await fetch(target, init);
  } catch (e) {
    const gap = switchingGap(0);
    if (gap) throw maintenanceError({ status: 0 }, null, gap.m);
    throw e;
  }
  if (res.status === 502 || res.status === 504) {
    const gap = switchingGap(res.status);
    if (gap) throw maintenanceError(res, null, gap.m);
  }
  return parse(res);
}

export async function qbaseGet(path, params) {
  return send(url(path, params), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
}

export async function qbasePost(path, body) {
  return send(url(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json; charset=UTF-8', Accept: 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

/** 조회 API 의 행 목록만 꺼낸다 (등록 API 는 List 를 표준 래핑해서 돌려준다) */
export async function qbaseSelect(path, params) {
  const body = await qbaseGet(path, params);
  if (Array.isArray(body)) return body;
  return body?.resultList || body?.data || [];
}

// ── 인증 · 세션 ────────────────────────────────────────────────────────────

/**
 * viewOnly 정규화 — 두 응답의 표기가 다르다.
 *   POST /qbase/api/login  → boolean (`!editor`)
 *   GET  /qbase/api/session → 세션 문자열 `"Y"` / `"N"`
 * 그냥 `!!v` 로 읽으면 `"N"` 이 참이 되어 **정상 세션이 전부 조회 전용으로 보인다.**
 */
export function isViewOnly(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v === 'Y' || v.toLowerCase() === 'true';
  return false;
}

/**
 * 로그인. qbaseSysId 를 **반드시** 실어 보낸다 (미지정 시 서버가 QSY0000005 로 폴백한다).
 *
 * 편집권은 사용자당 1세션이라, 다른 창·다른 PC·검증 스크립트가 이미 쥐고 있으면
 * 로그인은 성공하되 viewOnly 로 떨어진다. 같은 디바이스의 좀비 세션이면 승계할 수 있으므로
 * 한 번 시도하고, 그래도 안 되면 **조용히 넘어가지 않고 상태로 알린다** — 저장이 403 으로
 * 실패하는 것보다 "지금 조회 전용" 이라고 미리 보이는 편이 낫다.
 */
export async function login(loginId, pwd) {
  const res = await qbasePost(AUTH_API.login, {
    loginId, pwd, qbaseSysId: QBASE_SYS_ID, deviceId: DEVICE_ID,
  });
  let viewOnly = isViewOnly(res?.user?.viewOnly);
  if (viewOnly) viewOnly = !(await acquireEdit());
  return { user: res?.user || null, viewOnly };
}

// ── 체험 계정 ─────────────────────────────────────────────────────────────
// 비밀번호 없이 들어가는 체험 계정(edu01 …). 로그인은 **LiveSQL 서버가 대신** 하고(비밀번호는 서버 설정에만 있다)
// QBase 세션 쿠키를 내려 준다 — 같은 오리진이라 그 뒤는 일반 로그인과 같다(backend/modules/trial).
const LIVESQL_API = process.env.REACT_APP_API_BASE_URL || '/sql-developer';

/** 로그인 화면에 보일 체험 계정 — 꺼져 있거나 못 받으면 빈 목록(단추를 그리지 않는다) */
export async function trialAccounts() {
  try {
    const res = await fetch(`${LIVESQL_API}/trial/accounts`, { credentials: 'include', headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body?.accounts) ? body.accounts : [];
  } catch {
    return [];
  }
}

/** 체험 계정으로 로그인 — 응답 모양은 login() 과 같다({ user, viewOnly }) */
export async function trialLogin(loginId) {
  const res = await fetch(`${LIVESQL_API}/trial/login`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ loginId, qbaseSysId: QBASE_SYS_ID, deviceId: DEVICE_ID }),
  });
  const body = await res.json().catch(() => ({}));
  // QBase 점검 — 로그인 실패가 아니라 「지금은 점검 중」 이다(LiveSQL 서버가 503 MAINTENANCE 로 넘긴다)
  const m = detectMaintenance(res.status, body, headerPhaseOf(res));
  if (m) throw maintenanceError(res, body, m);
  if (!res.ok || !body?.success) {
    const err = new Error(body?.message || i18n.t(`auth:trial.err.${body?.errorType || 'failed'}`, { defaultValue: i18n.t('auth:trial.err.failed') }));
    err.status = res.status;
    throw err;
  }
  let viewOnly = isViewOnly(body?.user?.viewOnly);
  if (viewOnly) viewOnly = !(await acquireEdit());
  return { user: body.user || null, viewOnly };
}

/**
 * 소셜 로그인 — 제공자에게서 받은 자격증명을 QBase 에 넘겨 **같은 세션**을 만든다.
 *
 * 일반 로그인과 다른 점은 넘기는 값뿐이다(Google=access_token, Naver=code+state).
 * 세션·편집권은 같은 규칙이라 **뒤처리를 함께 둔다** — 여기가 갈리면 소셜로 들어온
 * 사용자만 조용히 조회 전용이 되는 날이 온다.
 *
 * 탈회 계정이면 QBase 가 200 + `success:false, withdrawn:true` 로 답한다. `parse()` 가
 * 그것을 던지면서 본문을 `err.body` 에 붙여 주므로 화면이 복구/삭제를 물을 수 있다.
 *
 * **`created` 는 이 호출이 계정을 새로 만들었다는 뜻**이다(QBase 는 처음 온 사람을 자동으로
 * 가입시킨다). 응답만 보면 첫 로그인과 두 번째 로그인이 똑같이 생겨서, 이 한 칸이 없으면
 * 화면은 가입 마무리 단계를 띄울지 알 수 없다.
 */
async function oauthLogin(path, payload) {
  const res = await qbasePost(path, { ...payload, qbaseSysId: QBASE_SYS_ID, deviceId: DEVICE_ID });
  // OAuth 응답에는 viewOnly 가 없다. 편집권은 사용자당 1세션이라 여기서도 한 번 잡아 본다.
  let viewOnly = isViewOnly(res?.user?.viewOnly);
  if (viewOnly) viewOnly = !(await acquireEdit());
  return { user: res?.user || null, viewOnly, created: !!res?.created };
}

export const googleLogin = (accessToken) => oauthLogin(AUTH_API.googleLogin, { access_token: accessToken });
export const naverLogin = (code, state) => oauthLogin(AUTH_API.naverLogin, { code, state });

export async function logout() {
  try {
    await qbasePost(AUTH_API.logout, {});
  } catch (e) {
    // 이미 만료된 세션이면 로그아웃도 실패한다 — 화면은 어차피 로그아웃 상태로 간다
  }
}

/** 세션 조회. 비로그인은 **상태이지 오류가 아니므로** null 로 돌려준다. */
export async function getSession() {
  const res = await fetch(url(AUTH_API.session), {
    method: 'GET', credentials: 'include', headers: { Accept: 'application/json' },
  });
  if (res.status === 401 || res.status === 403) return null;
  try {
    const body = await parse(res);
    if (!body || body.success === false) return null;
    return body;
  } catch (e) {
    return null;
  }
}

/**
 * 세션 상태를 **세 갈래로** 답한다 — `active` · `none`(로그인 안 됨·만료) · `unknown`(확인할 수 없었다).
 *
 * getSession 은 "세션 없음" 과 "물어볼 수 없었다" 를 둘 다 null 로 돌려준다. 그것을 만료 판정에 쓰면
 * QBase 가 잠깐 5xx 를 내거나 네트워크가 끊긴 것만으로 사용자를 로그인 창으로 보낸다. 판정에는 이것을 쓴다.
 */
export async function checkSession() {
  let res;
  try {
    res = await fetch(url(AUTH_API.session), {
      method: 'GET', credentials: 'include', headers: { Accept: 'application/json' },
    });
  } catch (e) {
    return 'unknown';
  }
  // 점검 중(503 · 헤더)·재기동 중이면 'unknown' — 만료로 단정하지 않는다(로그인 창을 띄우지 않는다)
  const phase = headerPhaseOf(res);
  if (phase) noteMaintenance(null, phase);
  if (res.status === 401 || res.status === 403) return 'none';
  if (!res.ok) return 'unknown';
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    return 'unknown';
  }
  return body && body.user && body.success !== false ? 'active' : 'none';
}

/**
 * 작업 시스템 확정. LiveSQL 에는 시스템 선택 화면이 없으므로 로그인·새로고침 직후 한 번 고른다.
 * 이걸 빠뜨리면 읽기 스코프의 축이 정해지지 않아 조회가 조용히 0건이 된다.
 */
export async function ensureActiveSystem(homeSysId) {
  const sysId = homeSysId || QBASE_SYS_ID;
  try {
    await qbasePost(AUTH_API.selectSystem, { qbaseSysId: sysId });
    return true;
  } catch (e) {
    console.error('[qbase] 작업 시스템 확정 실패:', e.message);
    return false;
  }
}

/** 편집권 획득 시도. 성공 여부만 돌려준다(실패는 정상적인 결과다 — 남이 쥐고 있을 수 있다). */
export async function acquireEdit() {
  try {
    const res = await qbasePost(AUTH_API.acquireEdit, { deviceId: DEVICE_ID });
    return !!res?.acquired;
  } catch (e) {
    return false;
  }
}

export async function releaseEdit() {
  try {
    await qbasePost(AUTH_API.releaseEdit, {});
  } catch (e) {
    /* 반납 실패는 치명적이지 않다 — 세션 만료 시 자동 해제된다 */
  }
}

/** 세션 유지 (레이아웃 타이머에서 주기 호출) */
export async function keepSession() {
  try {
    await qbaseGet(AUTH_API.keepSession);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * 테넌트 사용자 등록·수정.
 *
 * 이메일 인증(/api/emailverif/*)은 **공통** 저장소(VW_EML_USER·VW_USER)에만 계정을 만든다.
 * 그런데 로그인은 **테넌트** 저장소(VW_QBASE_USER)를 본다. 그래서 가입 직후 이 호출로
 * 테넌트 쪽 계정을 만들어 주지 않으면 "가입은 됐는데 로그인이 안 되는" 상태가 된다.
 *
 * saveGubun 은 쿼리 파라미터다(본문이 아니다).
 */
export async function saveUser(payload, saveGubun = 'new') {
  const path = `${AUTH_API.saveUser}?saveGubun=${encodeURIComponent(saveGubun)}`;
  return qbasePost(path, { ...payload, qbaseSysId: QBASE_SYS_ID });
}

/**
 * 탈회한 계정을 되살린다 — 예전 데이터가 그대로 돌아온다.
 * 재가입 시 QBase 가 `withdrawn: true` 로 거절할 때 사용자가 고르는 두 갈래 중 하나다.
 */
export function restoreUser(usrid) {
  return qbasePost(AUTH_API.restoreUser, { usrid, qbaseSysId: QBASE_SYS_ID });
}

/** 탈회한 계정을 영구 삭제한다 — 되돌릴 수 없다. 지운 뒤 새로 가입한다. */
export function purgeUser(usrid) {
  return qbasePost(AUTH_API.purgeUser, { usrid, qbaseSysId: QBASE_SYS_ID });
}

/**
 * 공통 계정(이메일 인증) 엔드포인트 호출 — QBase 직결.
 * 응답 해석은 각 화면이 하던 방식을 유지하려고 raw Response 를 그대로 돌려준다.
 */
export function emailVerifFetch(path, body) {
  return fetch(`${QBASE_API}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
